import { z } from "zod";
import { createEndpoint, StandardSuccessResponseSchema } from "../createEndpoint";
import { HttpClient, QueryParams } from "../httpClient";
import {
    ClientProfileSchema,
    UpdateClientProfileRequest,
    ChangePasswordRequest,
    RequestEmailChangeRequest,
    EmailChangeRequestResponseSchema,
    VerifyEmailChangeRequest,
    VerifyEmailChangeResponseSchema,
} from "../schemas/client-profile";

export function createClientProfileApi(http: HttpClient, baseUrl: string) {
    const clientBaseUrl = `${baseUrl}/client`;
    const securityBaseUrl = `${baseUrl}/client/security`;

    return {
        /**
         * Get the authenticated client's profile
         * GET /api/v2/client/profile
         */
        getProfile: createEndpoint<
            z.infer<typeof ClientProfileSchema>,
            unknown,
            QueryParams
        >(http, "GET", "/profile", {
            base: clientBaseUrl,
            responseSchema: ClientProfileSchema,
        }),

        /**
         * Update the authenticated client's profile
         * POST /api/v2/client/profile
         */
        updateProfile: createEndpoint<
            z.infer<typeof ClientProfileSchema>,
            UpdateClientProfileRequest,
            QueryParams
        >(http, "POST", "/profile", {
            base: clientBaseUrl,
            responseSchema: ClientProfileSchema,
        }),

        /**
         * Change the authenticated user's password
         * POST /api/v2/client/security/change-password
         */
        changePassword: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            ChangePasswordRequest,
            QueryParams
        >(http, "POST", "/change-password", {
            base: securityBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
        }),

        /**
         * Request an email change (sends verification to new email)
         * POST /api/v2/client/security/request-email-change
         */
        requestEmailChange: createEndpoint<
            z.infer<typeof EmailChangeRequestResponseSchema>,
            RequestEmailChangeRequest,
            QueryParams
        >(http, "POST", "/request-email-change", {
            base: securityBaseUrl,
            responseSchema: EmailChangeRequestResponseSchema,
        }),

        /**
         * Verify email change (public endpoint - no auth required)
         * POST /api/v2/client/security/verify-email-change
         */
        verifyEmailChange: createEndpoint<
            z.infer<typeof VerifyEmailChangeResponseSchema>,
            VerifyEmailChangeRequest,
            QueryParams
        >(http, "POST", "/verify-email-change", {
            base: securityBaseUrl,
            responseSchema: VerifyEmailChangeResponseSchema,
        }),
    };
}

export type ClientProfileApi = ReturnType<typeof createClientProfileApi>;

